import SideBlockAds from "@components/Blocks/Container/SideBlockAds";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import { Route } from "@route-types/Search/+types/SearchResult";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import NoResult from "./NoResult";
import {
  fetchSearchPageData,
  type SearchErrorResponseType,
  type SearchPayloadResponseType,
} from "./Search.server";
import SearchMetaData from "./SearchMetaData";
import SearchResultList from "./SearchResultList";

export function meta({
  loaderData,
}: {
  loaderData: TRouteWithoutRedirect<SearchPayloadResponseType, string>;
}) {
  const payload = loaderData.payload;

  if (typeof payload !== "object" || !("data" in payload)) return [];
  return [getMetaData(SearchMetaData(payload.searchedText).metaTags)];
}

export default function SearchResultPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    SearchPayloadResponseType,
    SearchErrorResponseType
  > = useLoaderData();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const searchedText: string = dataLoaderResponse.payload.searchedText;
  const keywordArticles: ProcessedArticleData[] | [] =
    dataLoaderResponse.payload.data;

  const adsSectionName = "search";

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  const basePageProps: BHBasePageProps = {
    uniqueSectionName: adsSectionName,
    pageAdTargetValue: pageAdTargetValue,
  };

  return (
    <BHBasePage {...basePageProps}>
      <div className="max-w-screen-cxl m-auto flex flex-col lg:flex-row w-full gap-4">
        {keywordArticles.length === 0 ? (
          <NoResult searchedText={searchedText} />
        ) : (
          <SearchResultList
            articles={keywordArticles}
            searchedText={searchedText}
            uniqueSectionName={adsSectionName}
          />
        )}
        <SideBlockAds
          adsSectionName={adsSectionName}
          adsOnly={true}
          pageAdTargetingProps={pageAdTargetValue}
          className="flex-1"
        />
      </div>
    </BHBasePage>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  const data = await fetchSearchPageData(params.searchValue);
  const { gaData } = SearchMetaData(params.searchValue);

  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<SearchPayloadResponseType | null, string>
  >;
}
